import { useState, useEffect } from 'react';
import { X, Download, Share, Plus, Smartphone, ExternalLink, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showManualInstructions, setShowManualInstructions] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if user has dismissed the prompt before
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    
    // Show prompt immediately on first visit, or after 7 days since last dismiss
    if (!standalone && !dismissed) {
      // Show immediately for first-time visitors
      setTimeout(() => setShowPrompt(true), 500);
    } else if (!standalone && dismissed) {
      const dismissedTime = parseInt(dismissed);
      const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed > 7) {
        setTimeout(() => setShowPrompt(true), 2000);
      }
    }

    // For non-iOS, listen for beforeinstallprompt
    if (!iOS) {
      const handler = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
      };
      window.addEventListener('beforeinstallprompt', handler);
      return () => window.removeEventListener('beforeinstallprompt', handler);
    }
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log('PWA install outcome:', outcome);
      setDeferredPrompt(null);
      setShowPrompt(false);
      localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
    } else {
      // No native prompt available, show manual instructions
      setShowManualInstructions(true);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
    setShowPrompt(false);
  };

  const handleContinueInBrowser = () => {
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
    setShowPrompt(false);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Don't show if already installed or prompt not triggered
  if (isStandalone || !showPrompt) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-300 p-4">
      <div 
        className="bg-white rounded-3xl shadow-2xl border-2 p-4 sm:p-6 max-w-sm w-full animate-in zoom-in-95 duration-300 max-h-[90vh] overflow-y-auto"
        style={{ borderColor: '#6FCFD3' }}
      >
        {/* Header with icon */}
        <div className="text-center mb-4 sm:mb-6">
          <div 
            className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg overflow-hidden"
            style={{ backgroundColor: '#6FCFD3' }}
          >
            <img 
              src="/icons/icon-192.png" 
              alt="Wild Mustang Life" 
              className="w-full h-full object-cover"
            />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">Wild Mustang Life</h2>
          <p className="text-gray-500 text-xs sm:text-sm">Your mustang adoption journey awaits</p>
        </div>

        {/* Install prompt content */}
        <div className="space-y-4">
          {isIOS ? (
            // iOS Instructions
            <>
              <div className="text-center mb-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium mb-2">
                  <Smartphone className="w-3 h-3" />
                  iPhone/iPad
                </div>
                <p className="text-sm text-gray-600 font-medium">
                  Add to your home screen for the best experience!
                </p>
              </div>
              
              <div className="space-y-3 bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full text-white flex-shrink-0" style={{ backgroundColor: '#6FCFD3' }}>
                    <span className="font-bold">1</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 flex items-center gap-2">
                      Tap the Share button
                      <Share className="w-5 h-5 text-blue-500" />
                    </p>
                    <p className="text-xs text-gray-500">At the bottom of Safari</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full text-white flex-shrink-0" style={{ backgroundColor: '#6FCFD3' }}>
                    <span className="font-bold">2</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 flex items-center gap-2">
                      Tap "Add to Home Screen"
                      <Plus className="w-5 h-5 text-gray-700" />
                    </p>
                    <p className="text-xs text-gray-500">Scroll down in the menu</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full text-white flex-shrink-0" style={{ backgroundColor: '#6FCFD3' }}>
                    <span className="font-bold">3</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Tap "Add"</p>
                    <p className="text-xs text-gray-500">App appears on your home screen!</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2 pt-2">
                <Button 
                  onClick={handleDismiss}
                  className="w-full h-12 text-base font-semibold"
                  style={{ backgroundColor: '#6FCFD3' }}
                >
                  Got it, I'll install!
                </Button>
                <Button 
                  onClick={handleContinueInBrowser}
                  variant="ghost"
                  className="w-full text-gray-500"
                >
                  Continue in browser
                </Button>
              </div>
            </>
          ) : showManualInstructions ? (
            // Manual instructions for Android/Desktop when native prompt isn't available
            <>
              <div className="text-center mb-4">
                <p className="text-sm text-gray-600 font-medium">
                  Follow these steps to install:
                </p>
              </div>
              
              <div className="space-y-3 bg-gray-50 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full text-white flex-shrink-0 text-sm" style={{ backgroundColor: '#6FCFD3' }}>
                    <span className="font-bold">1</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Open browser menu</p>
                    <p className="text-xs text-gray-500">Tap the ⋮ or ⋯ icon in your browser</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full text-white flex-shrink-0 text-sm" style={{ backgroundColor: '#6FCFD3' }}>
                    <span className="font-bold">2</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Find install option</p>
                    <p className="text-xs text-gray-500">Look for "Install app", "Add to Home screen", or "Install Wild Mustang"</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full text-white flex-shrink-0 text-sm" style={{ backgroundColor: '#6FCFD3' }}>
                    <span className="font-bold">3</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Confirm installation</p>
                    <p className="text-xs text-gray-500">Tap "Install" or "Add" to confirm</p>
                  </div>
                </div>
              </div>

              {/* Copy link option */}
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-xs text-blue-700 mb-2">Or copy this link to open in a different browser:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-white px-2 py-1 rounded border truncate">
                    {window.location.origin}
                  </code>
                  <Button
                    onClick={copyLink}
                    size="sm"
                    variant="outline"
                    className="flex-shrink-0"
                  >
                    {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              <div className="space-y-2 pt-2">
                <Button 
                  onClick={() => setShowManualInstructions(false)}
                  variant="outline"
                  className="w-full"
                >
                  Back
                </Button>
                <Button 
                  onClick={handleContinueInBrowser}
                  variant="ghost"
                  className="w-full text-gray-500"
                >
                  Continue in browser
                </Button>
              </div>
            </>
          ) : (
            // Android/Desktop - Direct install button
            <>
              <div className="text-center space-y-2 mb-4">
                <div className="flex items-center justify-center gap-2 text-green-600">
                  <Smartphone className="w-5 h-5" />
                  <span className="text-sm font-medium">Install for free</span>
                </div>
                <p className="text-sm text-gray-600">
                  Get quick access from your home screen with offline support!
                </p>
              </div>

              <div className="space-y-2">
                <Button 
                  onClick={handleInstall}
                  className="w-full h-12 text-base font-semibold"
                  style={{ backgroundColor: '#6FCFD3' }}
                >
                  <Download className="w-5 h-5 mr-2" />
                  Install Wild Mustang
                </Button>
                <Button 
                  onClick={handleContinueInBrowser}
                  variant="ghost"
                  className="w-full text-gray-500"
                >
                  Continue in browser
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Features */}
        <div className="mt-6 pt-4 border-t border-gray-100">
          <div className="grid grid-cols-3 gap-2 text-center text-xs text-gray-500">
            <div>
              <div className="text-lg mb-1">⚡</div>
              <span>Fast Access</span>
            </div>
            <div>
              <div className="text-lg mb-1">📱</div>
              <span>Home Screen</span>
            </div>
            <div>
              <div className="text-lg mb-1">🔔</div>
              <span>Updates</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
